/**
 * Translation keys (add to globalI18n or instanceI18n):
 * - bulkAddToTrash
 * - bulkAddToTrashConfirmTitle
 * - bulkAddToTrashConfirmDescription
 * - bulkAddToTrashSuccess
 * - bulkAddToTrashSuccessDescription
 * - bulkAddToTrashPartial
 * - bulkAddToTrashPartialDescription
 * - bulkAddToTrashError
 * - bulkAddToTrashErrorDescription
 */

import { useBulkAddToTrash } from "@/api/trash";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type BulkAddToTrashButtonProps = {
  selectedIds: string[];
  documentType: string;
  onSuccess?: () => void;
  disabled?: boolean;
};

const BulkAddToTrashButton = ({
  selectedIds,
  documentType,
  onSuccess,
  disabled = false,
}: BulkAddToTrashButtonProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader";
  const [open, setOpen] = useState(false);
  const bulkAddToTrash = useBulkAddToTrash();

  if (isReader || selectedIds.length === 0) {
    return null;
  }

  const handleConfirm = () => {
    const items = selectedIds.map((documentId) => ({
      documentId,
      documentType,
    }));

    bulkAddToTrash.mutate(items, {
      onSuccess: (result) => {
        setOpen(false);

        if (result.succeeded.length > 0 && result.failed.length === 0) {
          toast({
            title: t("bulkAddToTrashSuccess"),
            description: t("bulkAddToTrashSuccessDescription", {
              count: result.succeeded.length,
            }),
            variant: "success",
          });
          onSuccess?.();
        } else if (result.succeeded.length > 0 && result.failed.length > 0) {
          toast({
            title: t("bulkAddToTrashPartial"),
            description: t(
              "bulkAddToTrashPartialDescription",
              {
                succeeded: result.succeeded.length,
                failed: result.failed.length,
              },
            ),
            variant: "success",
          });
          onSuccess?.();
        } else {
          toast({
            title: t("bulkAddToTrashError"),
            description: t("bulkAddToTrashErrorDescription"),
            variant: "error",
          });
        }
      },
      onError: () => {
        setOpen(false);
        toast({
          title: t("bulkAddToTrashError"),
          description: t("bulkAddToTrashErrorDescription"),
          variant: "error",
        });
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          className="flex flex-row gap-2 items-center"
          disabled={disabled || bulkAddToTrash.isPending}
        >
          <Trash2 className="w-5 h-5 text-[#DC2626]" />
          {t("bulkAddToTrash")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-white">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("bulkAddToTrashConfirmTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "bulkAddToTrashConfirmDescription",
              { count: selectedIds.length },
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={bulkAddToTrash.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t("bulkAddToTrash")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BulkAddToTrashButton;
